import Scene from '../../scene/scene';
import Element, { ElementParams } from '../element';
import Ship from './ship';

import { getXByDistanceAndAngle, getYByDistanceAndAngle, getCorrectAngle } from '../../../utils/geometry';
import { Faction } from '../../../utils/faction';

export interface EquipmentParams extends ElementParams {
  ship?: Ship;
  angleFromShipCenter: number;
  distanceFromShipCenter: number;
  aboveShip?: boolean;
}

export default class Equipment extends Element implements EquipmentParams {
  ship!: Ship; // owner ship
  angleFromShipCenter = 90;
  distanceFromShipCenter = 0;
  aboveShip = false; // draw below or above the owner ship's sprite

  lookAngleRelative: number;
  lookAngleRelativeDefault: number;

  constructor(params: EquipmentParams) {
    super(params);
    this.applyParams(params);

    if (this.ship) {
      this.equipTo(this.ship);
      if (this.context === undefined)
        this.context = this.ship.context;
    }

    this.lookAngleRelative = this.lookAngle;
    this.lookAngleRelativeDefault = this.lookAngleRelative;
  }

  getFaction(): Faction {
    return this.ship.faction;
  }

  getScene(): Scene {
    return this.ship.scene;
  }

  getRelativeAngle(angle: number): number {
    return getCorrectAngle(angle + 90 - this.ship.lookAngle);
  }

  getAbsoluteAngle(angle: number): number {
    return getCorrectAngle(this.ship.lookAngle + angle - 90);
  }

  getFactionTargetGroups(): Array<Faction> {
    return this?.ship.targetsGroups ?? [];
  }

  equipTo(ship: Ship): Ship {
    this.ship = ship;
    return this.ship;
  }

  updatePosition(): void {
    /* Update position */
    this.x = getXByDistanceAndAngle({
      x: this.ship.x,
      angle: getCorrectAngle(this.ship.lookAngle + this.angleFromShipCenter - 90),
      distance: this.distanceFromShipCenter,
    });
    this.y = getYByDistanceAndAngle({
      y: this.ship.y,
      angle: getCorrectAngle(this.ship.lookAngle + this.angleFromShipCenter - 90),
      distance: this.distanceFromShipCenter,
    });
  }

  updateLookAngle(): void {
    this.lookAngle = this.getAbsoluteAngle(this.lookAngleRelative);
  }

  /* Overrriden function to update the lookAngleRelative as well */
  rotateTowardsAngle(angleToReach: number, modifier: number): number {
    const absoluteLookAngle = super.rotateTowardsAngle(angleToReach, modifier);
    this.lookAngleRelative = this.getRelativeAngle(this.lookAngle);
    return absoluteLookAngle;
  }

  update(modifier: number): Equipment {
    this.updatePosition();
    this.updateLookAngle();
    return this;
  }

  draw(): CanvasRenderingContext2D {
    const {
      sprite,
      ship: {
        context
      }
    } = this;

    if (!sprite)
      return context;

    context.save();
    this.prepareContext();
    if (!this.aboveShip)
      this.context.globalCompositeOperation = 'destination-over'; // draw under ship
    sprite.draw();
    context.restore();
    return context;
  }

}
